import React, { useCallback, useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, DOC_CATEGORIES } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { ArrowLeft, Plus, Trash2, Upload, FileText, Image as ImageIcon, Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const REPAIR_EMPTY = { repair_type: "", description: "", vendor: "", mechanic: "", parts_cost: 0, labor_cost: 0, tax: 0, date: new Date().toISOString().slice(0, 10) };

export default function VehicleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairForm, setRepairForm] = useState(REPAIR_EMPTY);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("vehicle_photo");
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const canEdit = user?.role === "admin" || user?.role === "employee";

  const load = useCallback(async () => {
    const r = await api.get(`/vehicles/${id}`);
    setData(r.data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const { vehicle: v, costs, sale, customer, documents, gross_profit, net_profit } = data;

  const addRepair = async () => {
    if (!repairForm.repair_type) { toast.error("Type required"); return; }
    await api.post("/repairs", { vehicle_id: id, ...repairForm,
      parts_cost: Number(repairForm.parts_cost), labor_cost: Number(repairForm.labor_cost), tax: Number(repairForm.tax) });
    setRepairOpen(false); setRepairForm(REPAIR_EMPTY); toast.success("Repair added"); load();
  };

  const delRepair = async (rid) => { await api.delete(`/repairs/${rid}`); toast.success("Removed"); load(); };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("vehicle_id", id);
      fd.append("category", uploadCategory);
      fd.append("label", uploadLabel);
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); setUploadOpen(false); setUploadLabel(""); load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const delDoc = async (did) => { await api.delete(`/documents/${did}`); load(); };

  const isImage = (ct) => (ct || "").startsWith("image/");
  const photos = documents.filter((d) => isImage(d.content_type));
  const files = documents.filter((d) => !isImage(d.content_type));

  const docUrl = (docId) => `${api.defaults.baseURL}/documents/${docId}/download?auth=${localStorage.getItem("mc_token")}`;

  const timeline = [
    { key: "purchased", label: "Purchased", date: v.purchase_date },
    { key: "in_transit", label: "In Transit", date: null },
    { key: "repairing", label: "Repairing", date: null },
    { key: "ready", label: "Ready for Sale", date: null },
    { key: "listed", label: "Listed", date: null },
    { key: "sold", label: "Sold", date: sale?.sale_date || sale?.created_at },
  ];
  const statusOrder = ["purchased", "in_transit", "repairing", "ready", "listed", "sold"];
  const currentIdx = statusOrder.indexOf(v.status);

  const profitColor = (() => {
    if (net_profit === null) return "text-muted-foreground";
    return net_profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400";
  })();

  return (
    <div data-testid="vehicle-detail-page">
      <Link to="/inventory" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} /> Back to Inventory
      </Link>

      <PageHeader
        overline={`Stock ${v.stock_number}`}
        title={`${v.year} ${v.make} ${v.model}`}
        description={`${v.trim || ""} · VIN ${v.vin || "—"} · ${Number(v.mileage || 0).toLocaleString()} mi · ${v.color || "—"}`}
        action={
          <div className="flex items-center gap-2">
            <Link to={`/deal-sheet/${v.id}`} data-testid="open-deal-sheet" className="px-3 py-2 rounded-lg border border-border bg-card hover:bg-secondary text-xs font-semibold uppercase tracking-wider">Deal Sheet</Link>
            <StatusBadge status={v.status} />
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total Investment</div>
          <div className="font-display text-2xl font-bold mt-1">{fmtMoney(costs.total_investment)}</div>
          <div className="text-xs text-muted-foreground mt-2">incl. {fmtMoney(costs.repair_total)} repairs</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Market Value</div>
          <div className="font-display text-2xl font-bold mt-1">{fmtMoney(v.market_value)}</div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Sale Price</div>
          <div className="font-display text-2xl font-bold mt-1">{sale ? fmtMoney(sale.sale_price) : "—"}</div>
          {sale && <Link to={`/sales/${sale.id}`} className="text-xs text-blue-600 hover:underline mt-2 inline-block">View Invoice →</Link>}
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Net Profit</div>
          <div className={`font-display text-2xl font-bold mt-1 ${profitColor}`}>
            {net_profit === null ? "—" : fmtMoney(net_profit)}
          </div>
          {gross_profit !== null && <div className="text-xs text-muted-foreground mt-2">Gross {fmtMoney(gross_profit)}</div>}
        </Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-lg">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-md">Overview</TabsTrigger>
          <TabsTrigger value="repairs" data-testid="tab-repairs" className="rounded-md">Repairs ({costs.repairs.length})</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos" className="rounded-md">Photos ({photos.length})</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents" className="rounded-md">Documents ({files.length})</TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline" className="rounded-md">Timeline</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card className="p-5">
            <h3 className="font-display text-lg font-semibold mb-4">Vehicle Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-6 text-sm">
              {[
                ["Engine", v.engine], ["Transmission", v.transmission], ["Fuel", v.fuel_type], ["Title", v.title_status],
                ["Auction House", v.auction_house], ["Purchase Location", v.purchase_location], ["Seller", v.seller_info], ["Purchase Date", fmtDate(v.purchase_date)],
              ].map(([l, val]) => (
                <div key={l}>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
                  <div className="font-medium">{val || "—"}</div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-6 text-sm mt-6 pt-6 border-t border-border">
              {[
                ["Purchase", v.purchase_price], ["Auction Fees", v.auction_fees], ["Transport", v.transport_fees],
                ["Gate", v.gate_fees], ["Doc Fees", v.documentation_fees], ["Storage", v.storage_fees],
                ["Buyer Fees", v.buyer_fees], ["Misc", v.misc_costs],
              ].map(([l, val]) => (
                <div key={l}>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
                  <div className="font-medium">{fmtMoney(val)}</div>
                </div>
              ))}
            </div>
            {v.notes && <div className="mt-6 pt-6 border-t border-border text-sm whitespace-pre-wrap"><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Notes</div>{v.notes}</div>}
          </Card>
        </TabsContent>

        <TabsContent value="repairs" className="mt-4">
          {canEdit && (
            <div className="mb-3 flex justify-end">
              <Button data-testid="add-repair-button" onClick={() => setRepairOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><Plus size={14} className="mr-2" />Add Repair</Button>
            </div>
          )}
          <Card className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
                <tr>{["Date", "Type", "Description", "Vendor", "Mechanic", "Parts", "Labor", "Tax", "Total", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {costs.repairs.length === 0 && <tr><td colSpan={10} className="p-10 text-center text-muted-foreground">No repairs logged.</td></tr>}
                {costs.repairs.map((r) => (
                  <tr key={r.id} data-testid={`repair-row-${r.id}`} className="border-t border-border hover:bg-secondary/40">
                    <td className="px-4 py-3">{fmtDate(r.date || r.created_at)}</td>
                    <td className="px-4 py-3 font-medium">{r.repair_type}</td>
                    <td className="px-4 py-3 text-muted-foreground">{r.description}</td>
                    <td className="px-4 py-3">{r.vendor || "—"}</td>
                    <td className="px-4 py-3">{r.mechanic || "—"}</td>
                    <td className="px-4 py-3">{fmtMoney(r.parts_cost)}</td>
                    <td className="px-4 py-3">{fmtMoney(r.labor_cost)}</td>
                    <td className="px-4 py-3">{fmtMoney(r.tax)}</td>
                    <td className="px-4 py-3 font-semibold">{fmtMoney(r.parts_cost + r.labor_cost + r.tax)}</td>
                    <td className="px-4 py-3 text-right">{canEdit && <button data-testid={`del-repair-${r.id}`} onClick={() => delRepair(r.id)} className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600"><Trash2 size={14} /></button>}</td>
                  </tr>
                ))}
                {costs.repairs.length > 0 && (
                  <tr className="border-t-2 border-foreground bg-secondary">
                    <td colSpan={8} className="px-4 py-3 text-right text-xs uppercase font-semibold">Total Repairs</td>
                    <td className="px-4 py-3 font-bold">{fmtMoney(costs.repair_total)}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="photos" className="mt-4">
          {canEdit && (
            <div className="mb-3 flex justify-end">
              <Button data-testid="upload-photo-button" onClick={() => { setUploadCategory("vehicle_photo"); setUploadOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><Upload size={14} className="mr-2" />Upload Photo</Button>
            </div>
          )}
          {photos.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground">No photos uploaded.</Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((p) => (
                <Card key={p.id} className="overflow-hidden group">
                  <div className="aspect-square bg-secondary relative">
                    <img src={docUrl(p.id)} alt={p.label || p.original_filename} className="w-full h-full object-cover" />
                    <button onClick={() => delDoc(p.id)} className="absolute top-2 right-2 p-1.5 rounded-md bg-white/90 dark:bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity text-red-600"><Trash2 size={14} /></button>
                  </div>
                  <div className="p-3 text-xs">
                    <div className="font-semibold truncate">{p.label || p.original_filename}</div>
                    <div className="text-muted-foreground capitalize">{p.category.replace(/_/g, " ")}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          {canEdit && (
            <div className="mb-3 flex justify-end">
              <Button data-testid="upload-doc-button" onClick={() => { setUploadCategory("title"); setUploadOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><Upload size={14} className="mr-2" />Upload Document</Button>
            </div>
          )}
          {files.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground">No documents uploaded.</Card>
          ) : (
            <Card className="divide-y divide-border">
              {files.map((f) => (
                <div key={f.id} className="flex items-center gap-3 p-4">
                  <FileText size={20} className="text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{f.label || f.original_filename}</div>
                    <div className="text-xs text-muted-foreground capitalize">{f.category.replace(/_/g, " ")} · {fmtDate(f.created_at)}</div>
                  </div>
                  <a href={docUrl(f.id)} target="_blank" rel="noreferrer" className="p-1.5 rounded-md hover:bg-secondary"><Download size={14} /></a>
                  {canEdit && <button onClick={() => delDoc(f.id)} className="p-1.5 rounded-md hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>}
                </div>
              ))}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="timeline" className="mt-4">
          <Card className="p-6">
            <div className="space-y-4">
              {timeline.map((t, i) => {
                const active = i <= currentIdx;
                return (
                  <div key={t.key} className="flex items-start gap-4">
                    <div className={`w-3 h-3 rounded-full mt-1 ${active ? "bg-blue-600" : "bg-secondary border-2 border-border"}`} />
                    <div className="flex-1 pb-4 border-b border-border last:border-0">
                      <div className={`font-medium ${active ? "" : "text-muted-foreground"}`}>{t.label}</div>
                      {t.date && <div className="text-xs text-muted-foreground mt-0.5">{fmtDate(t.date)}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Repair Dialog */}
      <Dialog open={repairOpen} onOpenChange={setRepairOpen}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Add Repair</DialogTitle>
            <DialogDescription>Log parts, labor and tax separately for accurate cost tracking.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Type</Label><Input data-testid="r-type" value={repairForm.repair_type} onChange={(e) => setRepairForm({ ...repairForm, repair_type: e.target.value })} placeholder="e.g. Suspension Repair" className="rounded-lg" /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea data-testid="r-description" value={repairForm.description} onChange={(e) => setRepairForm({ ...repairForm, description: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Vendor</Label><Input data-testid="r-vendor" value={repairForm.vendor} onChange={(e) => setRepairForm({ ...repairForm, vendor: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Mechanic</Label><Input data-testid="r-mechanic" value={repairForm.mechanic} onChange={(e) => setRepairForm({ ...repairForm, mechanic: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Parts Cost</Label><Input data-testid="r-parts" type="number" value={repairForm.parts_cost} onChange={(e) => setRepairForm({ ...repairForm, parts_cost: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Labor Cost</Label><Input data-testid="r-labor" type="number" value={repairForm.labor_cost} onChange={(e) => setRepairForm({ ...repairForm, labor_cost: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Tax</Label><Input data-testid="r-tax" type="number" value={repairForm.tax} onChange={(e) => setRepairForm({ ...repairForm, tax: e.target.value })} className="rounded-lg" /></div>
            <div><Label>Date</Label><Input data-testid="r-date" type="date" value={repairForm.date} onChange={(e) => setRepairForm({ ...repairForm, date: e.target.value })} className="rounded-lg" /></div>
          </div>
          <DialogFooter><Button data-testid="r-save" onClick={addRepair} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Save Repair</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Upload File</DialogTitle>
            <DialogDescription>Photos, titles, invoices, receipts. Stored securely.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger data-testid="upload-category" className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_CATEGORIES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Label (optional)</Label><Input data-testid="upload-label" value={uploadLabel} onChange={(e) => setUploadLabel(e.target.value)} className="rounded-lg" /></div>
            <input data-testid="upload-file" ref={fileRef} type="file" onChange={uploadDoc} className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700" />
            {uploading && <div className="text-sm text-muted-foreground">Uploading…</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
